import React, { useState, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import validator from 'validator';
import { useAuth } from '../../providers/auth_provider';

export default function EditCategory({ categoryId, fetchCategories }) {
    const { token } = useAuth();
    const [errors, setErrors] = useState('');
    const [categoryData, setCategoryData] = useState('');
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        keywords: '',
        image: null
    });

    useLayoutEffect(() => {
        if (categoryId) {
            const getCategoryData = async () => {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_API_URL}/category/${categoryId}`, {
                        headers: {
                            'Content-Type': 'application/json',
                            Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    const fetchedCategory = response?.data?.category;
                    if (fetchedCategory) {
                        setCategoryData(fetchedCategory);
                        if (fetchedCategory.image) {
                            setImagePreview(fetchedCategory.image);
                        }
                    } else {
                        toast.error('Category not found');
                    }
                } catch (error) {
                    console.error('Error fetching category:', error);
                    toast.error('Error fetching category data');
                }
            }
            getCategoryData();
        }
    }, [categoryId]);

    useEffect(() => {
        if (categoryData) {
            setFormData({
                name: categoryData.name || '',
                description: categoryData.description || '',
                keywords: categoryData.keywords || '',
                image: null
            });
        }
    }, [categoryData]);

    const handleChange = (e) => {
        if (e.target.type === 'file') {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);

                setFormData({
                    ...formData,
                    image: file
                });
            }
        } else {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (validator.isEmpty(formData.name)) {
            newErrors.name = 'Please enter Category name';
        }
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) return;

        try {
            setLoading(true);
            const submitFormData = new FormData();
            submitFormData.append('name', formData.name);
            submitFormData.append('description', formData.description);
            submitFormData.append('keywords', formData.keywords);
            if (formData.image) {
                submitFormData.append('image', formData.image);
            }

            await axios.put(
                `${process.env.REACT_APP_API_URL}/category/update/${categoryData.id}`,
                submitFormData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            fetchCategories();
            document.getElementById('closeModal').click();
            toast.success('Category updated successfully');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="offcanvas offcanvas-end w-50" tabIndex={-1} id="offcanvasEditCategory" aria-labelledby="offcanvasAddUserLabel">
            <div className="offcanvas-header border-bottom">
                <h5 id="offcanvasAddUserLabel" className="offcanvas-title">
                    Edit Category
                </h5>
                <button type="button" id="closeModal" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
            </div>
            <div className="offcanvas-body mx-0 flex-grow-0 h-100">
                {categoryData ? (
                    <form className="add-new-user pt-0" id="addNewUserForm" onSubmit={handleSubmit}>
                        <div className="form-floating form-floating-outline mb-4">
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                id="add-category-name"
                                name="name"
                                aria-label="Name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                            <label htmlFor="add-category-name">Name</label>
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>
                        <div className="form-floating form-floating-outline mb-4">
                            <textarea
                                className={`h-50 form-control ${errors.description ? 'is-invalid' : ''}`}
                                id="add-category-description"
                                name="description"
                                aria-label="Description"
                                value={formData.description}
                                onChange={handleChange}
                            />
                            <label htmlFor="add-category-description">Description</label>
                        </div>

                        <div className="form-floating form-floating-outline mb-4">
                            <input
                                type="text"
                                className={`form-control ${errors.keywords ? 'is-invalid' : ''}`}
                                id="add-category-keywords"
                                name="keywords"
                                placeholder="Enter keywords separated by comma"
                                aria-label="Keywords"
                                value={formData.keywords}
                                onChange={handleChange}
                            />
                            <label htmlFor="add-category-keywords">Keywords (comma separated)</label>
                            {errors.keywords && <div className="invalid-feedback">{errors.keywords}</div>}
                        </div>

                        {/* Image Upload Section */}
                        <div className="mb-4">
                            <label className="form-label">Category Image</label>
                            <input
                                type="file"
                                className="form-control"
                                id="category-image"
                                name="image"
                                accept="image/*"
                                onChange={handleChange}
                            />
                            {imagePreview && (
                                <div className="mt-2">
                                    <img
                                        src={imagePreview}
                                        alt="Category preview"
                                        className="rounded"
                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                    />
                                </div>
                            )}
                        </div>

                        <hr />
                        <button
                            disabled={loading}
                            type="submit"
                            className="btn btn-success me-sm-3 me-1 data-submit"
                        >
                            {loading ? 'Please wait' : 'Submit'}
                        </button>
                        <button
                            disabled={loading}
                            type="reset"
                            className="btn btn-outline-secondary"
                            data-bs-dismiss="offcanvas"
                        >
                            Cancel
                        </button>
                    </form>
                ) : (
                    <div>Loading...</div>
                )}
            </div>
        </div>
    );
}